import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { pmsFrom } from '@/integrations/pms';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHero } from '@/components/common/PageHero';
import { PageSidebar } from '@/components/common/PageSidebar';
import { SEOHead } from '@/components/seo/SEOHead';
import { FAQJsonLd } from '@/components/seo/JsonLd';
import { FAQ } from '@/components/common/FAQ';
import { LeadForm } from '@/components/forms/LeadForm';
import DynamicIcon from '@/components/common/DynamicIcon';
import { sanitizeHtml } from '@/lib/sanitize';
import DynamicPage from '@/pages/DynamicPage';

const ServiceSegmentPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: segment, isLoading } = useQuery({
    queryKey: ['service-segment', slug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('service_segments').select('*').eq('slug', slug!).eq('is_active', true).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: childCategories = [] } = useQuery({
    queryKey: ['segment-categories', segment?.id],
    queryFn: async () => {
      const { data } = await pmsFrom('service_categories').select('id, name, slug, icon, description').eq('segment_id', segment!.id).eq('is_active', true).order('sort_order');
      return data || [];
    },
    enabled: !!segment?.id,
  });

  const { data: businessSettings } = useQuery({
    queryKey: ['business-settings-public'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings').select('company_name, phone, industry').limit(1).maybeSingle();
      return data;
    },
  });

  const companyName = businessSettings?.company_name || 'Our Company';

  if (isLoading) {
    return <div className="min-h-screen"><div className="container mx-auto px-4 py-24 space-y-6"><Skeleton className="h-12 w-3/4" /><Skeleton className="h-64 w-full" /></div></div>;
  }

  // If no segment found, fall back to DynamicPage
  if (!segment) return <DynamicPage />;

  const faq = Array.isArray(segment.faq) ? segment.faq as Array<{ question: string; answer: string }> : [];

  return (
    <>
      <SEOHead
        title={segment.meta_title || `${segment.name} Services | ${companyName}`}
        description={segment.meta_description || segment.description || ''}
      />
      {faq.length > 0 && <FAQJsonLd faqs={faq} />}

      <PageHero title={segment.name} subtitle={segment.description || ''} badge="Services" size="md" showRating />

      <div className="container mx-auto px-4 py-4">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/services" className="hover:text-primary transition-colors">Services</Link>
          <span>/</span>
          <span className="text-foreground font-medium">{segment.name}</span>
        </nav>
      </div>

      <div className="container mx-auto px-4 py-8 pb-24">
        <div className="grid lg:grid-cols-3 gap-12">
          <main className="lg:col-span-2 space-y-12" role="main">
            {segment.content && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="prose prose-lg max-w-none dark:prose-invert seo-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(segment.content) }} />
            )}

            {childCategories.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl font-bold mb-6">Our {segment.name} Service Categories</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {childCategories.map(cat => (
                    <Link key={cat.id} to={`/${slug}/${cat.slug}`} className="group p-5 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-lg transition-all">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
                          <DynamicIcon name={cat.icon || 'Wrench'} className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
                        </div>
                        <div>
                          <h3 className="font-semibold group-hover:text-primary transition-colors">{cat.name}</h3>
                          {cat.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{cat.description}</p>}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary ml-auto shrink-0 mt-1" />
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {faq.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl font-bold mb-6">FAQs — {segment.name}</h2>
                <FAQ faqs={faq} />
              </section>
            )}

            <section className="bg-card rounded-xl border border-border p-8">
              <h2 className="font-heading text-2xl font-bold mb-2">Get a Free Estimate</h2>
              <p className="text-muted-foreground mb-6">Contact us for expert {segment.name} services.</p>
              <LeadForm source={`segment-${segment.slug}`} />
            </section>
          </main>

          <aside className="lg:col-span-1">
            <div className="sticky top-28">
              <PageSidebar showCta showTrustBadges showPromo showServiceAreas showRecentPosts ctaTitle={`Need ${segment.name}?`} ctaDescription={`Contact ${companyName} for professional ${segment.name} services.`} />
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default ServiceSegmentPage;